import { useEffect } from 'react'
import { faqs } from './faqData'

const TITLE =
  'Megapack Nutrición Resuelta | Sistema de organización para nutricionistas'
const DESCRIPTION =
  'Megapack Nutrición Resuelta: 6 tomos + 5 bonos profesionales para que nutricionistas ordenen sus consultas, mejoren seguimientos y dejen de improvisar. Acceso inmediato y garantía de 7 días.'
const KEYWORDS =
  'nutrición, nutricionistas, consultorio nutricional, organización de consultas, planes de alimentación, seguimiento de pacientes, megapack nutrición, Argentina'
const PRODUCT_NAME = 'Megapack Nutrición Resuelta'
const PRICE = '29990'
const CURRENCY = 'ARS'
const IMAGE =
  'https://res.cloudinary.com/dowzlbqlz/image/upload/f_auto,q_auto,w_640,c_limit/v1778382523/hf_20260509_035726_3b0b453a-cd00-4b5f-b77d-c15b35a1d726-Photoroom_1_g1m6xf.webp'

function upsertMeta(
  attr: 'name' | 'property',
  key: string,
  content: string,
): void {
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`,
  )
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function upsertLink(
  rel: string,
  href: string,
  extra?: Record<string, string>,
): void {
  const selector = `link[rel="${rel}"][href="${href}"]`
  if (document.head.querySelector(selector)) return
  const el = document.createElement('link')
  el.setAttribute('rel', rel)
  el.setAttribute('href', href)
  if (extra) {
    Object.entries(extra).forEach(([k, v]) => el.setAttribute(k, v))
  }
  document.head.appendChild(el)
}

function upsertJsonLd(id: string, data: unknown): void {
  let el = document.getElementById(id) as HTMLScriptElement | null
  if (!el) {
    el = document.createElement('script')
    el.type = 'application/ld+json'
    el.id = id
    document.head.appendChild(el)
  }
  el.textContent = JSON.stringify(data)
}

/**
 * Applies SEO metadata, social cards, performance resource hints and
 * structured data (Product + FAQ + Organization) to the document head.
 * Runs once on mount.
 */
export function useSeo(): void {
  useEffect(() => {
    if (typeof document === 'undefined') return

    const url = window.location.href

    // Language
    document.documentElement.lang = 'es-AR'

    // Title + primary meta
    document.title = TITLE
    upsertMeta('name', 'description', DESCRIPTION)
    upsertMeta('name', 'keywords', KEYWORDS)
    upsertMeta('name', 'robots', 'index, follow, max-image-preview:large')
    upsertMeta('name', 'theme-color', '#15803d')
    upsertMeta(
      'name',
      'viewport',
      'width=device-width, initial-scale=1, viewport-fit=cover',
    )

    // Canonical
    upsertLink('canonical', url)

    // Open Graph
    upsertMeta('property', 'og:type', 'product')
    upsertMeta('property', 'og:title', TITLE)
    upsertMeta('property', 'og:description', DESCRIPTION)
    upsertMeta('property', 'og:image', IMAGE)
    upsertMeta('property', 'og:url', url)
    upsertMeta('property', 'og:locale', 'es_AR')
    upsertMeta('property', 'og:site_name', PRODUCT_NAME)

    // Twitter card
    upsertMeta('name', 'twitter:card', 'summary_large_image')
    upsertMeta('name', 'twitter:title', TITLE)
    upsertMeta('name', 'twitter:description', DESCRIPTION)
    upsertMeta('name', 'twitter:image', IMAGE)

    // Performance: only preconnect to the LCP image origin (Cloudinary).
    // The Meta Pixel origin is intentionally NOT preconnected — it loads lazily
    // off the critical path, so an early handshake would only steal bandwidth
    // from the hero image during initial load.
    upsertLink('preconnect', 'https://res.cloudinary.com', {
      crossorigin: '',
    })
    upsertLink('dns-prefetch', 'https://res.cloudinary.com')

    // Structured data — Product offer
    upsertJsonLd('ld-product', {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: PRODUCT_NAME,
      image: [IMAGE],
      description: DESCRIPTION,
      brand: {
        '@type': 'Brand',
        name: 'Biblioteca Profesional',
      },
      offers: {
        '@type': 'Offer',
        url,
        priceCurrency: CURRENCY,
        price: PRICE,
        availability: 'https://schema.org/InStock',
        itemCondition: 'https://schema.org/NewCondition',
      },
    })

    // Structured data — FAQ
    upsertJsonLd('ld-faq', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: f.answer,
        },
      })),
    })

    // Structured data — Organization
    upsertJsonLd('ld-org', {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'Biblioteca Profesional',
      url,
      logo: IMAGE,
    })
  }, [])
}
